import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const STATE_TONE = {
  Draft: "muted", Inferred: "muted", Unverified: "warn",
  Approved: undefined, Restricted: "warn", Retired: "muted",
};

const ALLOWED = {
  Draft: ["Inferred", "Unverified", "Retired"],
  Inferred: ["Unverified", "Approved", "Restricted", "Retired"],
  Unverified: ["Approved", "Restricted", "Retired"],
  Approved: ["Restricted", "Retired"],
  Restricted: ["Approved", "Retired"],
  Retired: [],
};

export default function ProductDetail() {
  const { productId } = useParams();
  const { role } = useAuth();
  const canEdit = ["owner", "admin", "editor", "reviewer"].includes(role || "");
  const [data, setData] = useState({ loading: true, error: null, product: null, positioning: null, claims: [] });
  const [transitionErr, setTransitionErr] = useState(null);
  const [busyClaim, setBusyClaim] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/products/${productId}`);
      setData({ loading: false, error: null, ...res.data });
    } catch (e) {
      setData((s) => ({ ...s, loading: false, error: formatApiError(e) }));
    }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  async function doTransition(claim, toState) {
    setTransitionErr(null);
    setBusyClaim(claim.id);
    try {
      await api.post(`/claims/${claim.id}/transition`, { to_state: toState });
      await load();
    } catch (e) {
      setTransitionErr(`${claim.claim_text.slice(0, 40)}…: ${formatApiError(e)}`);
    } finally {
      setBusyClaim(null);
    }
  }

  const { product, positioning, claims } = data;

  return (
    <Shell title={product?.name ? `${product.name} — Product` : "Product"}>
      <Link to="/brand-os/products" className="bo-eyebrow" data-testid="back-to-products" style={{ textDecoration: "none" }}>
        ← All products
      </Link>
      <h1 className="bo-serif" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 22px" }} data-testid="product-title">
        {product?.name || "—"}
      </h1>

      <ErrorBanner error={data.error} />
      {data.loading && <LoadingCard />}

      {positioning && (
        <div className="bo-card" data-testid="positioning-card">
          <div className="bo-eyebrow">Positioning</div>
          <h2 className="bo-card-title">{positioning.promise || "—"}</h2>
          <p className="bo-card-lede">{positioning.value_proposition}</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            <div>
              <div className="bo-eyebrow" style={{ marginBottom: 4 }}>Problem</div>
              <div>{positioning.problem}</div>
            </div>
            <div>
              <div className="bo-eyebrow" style={{ marginBottom: 4 }}>Differentiator</div>
              <div>{positioning.differentiator}</div>
            </div>
          </div>
          {positioning.reasons_to_believe?.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div className="bo-eyebrow" style={{ marginBottom: 6 }}>Reasons to believe</div>
              <ul style={{ margin: 0, paddingLeft: 20, color: "var(--bo-ink-soft)" }}>
                {positioning.reasons_to_believe.map((r, i) => <li key={i} style={{ marginBottom: 4 }}>{r}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="bo-card" data-testid="claims-card">
        <div className="bo-eyebrow">Claims</div>
        <h2 className="bo-card-title">{claims?.length || 0} claim{claims?.length === 1 ? "" : "s"}</h2>
        <p className="bo-card-lede">
          Every approvable claim must cite active evidence. The Brand Guardian will refuse to ship a version that
          references an unbacked claim.
        </p>
        <ErrorBanner error={transitionErr} testId="claim-transition-error" />
        {(!claims || claims.length === 0) ? (
          <EmptyState title="No claims yet" description="Add claims here as you draft your positioning." />
        ) : (
          <div style={{ display: "grid", gap: 12 }} data-testid="claims-list">
            {claims.map((c) => {
              const canTransition = canEdit && ALLOWED[c.state]?.length > 0;
              return (
                <div
                  key={c.id}
                  style={{
                    border: "1px solid var(--bo-line)", borderRadius: "var(--bo-radius-sm)",
                    padding: "14px 16px", background: "var(--bo-panel-alt)",
                  }}
                  data-testid={`claim-row-${c.state.toLowerCase()}`}
                >
                  <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 6 }}><Pill tone={STATE_TONE[c.state]}>{c.state}</Pill></div>
                      <div style={{ fontSize: 14.5 }}>{c.claim_text}</div>
                      {c.required_disclaimer && (
                        <div style={{ marginTop: 8, fontSize: 12.5, color: "var(--bo-muted)", fontStyle: "italic" }}>
                          Required disclaimer: {c.required_disclaimer}
                        </div>
                      )}
                      {c.governance_warnings?.includes("last_evidence_removed") && (
                        <div style={{ marginTop: 8 }} data-testid={`claim-warning-${c.id}`}>
                          <Pill tone="warn">⚠ Last evidence removed — human review needed</Pill>
                        </div>
                      )}
                    </div>
                    {canTransition && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 180 }}>
                        {ALLOWED[c.state].map((next) => (
                          <button
                            key={next}
                            className="bo-btn bo-btn-ghost"
                            data-testid={`claim-transition-${c.id.slice(0,8)}-to-${next.toLowerCase()}`}
                            disabled={busyClaim === c.id}
                            onClick={() => doTransition(c, next)}
                            style={{ fontSize: 12.5, padding: "6px 10px", justifyContent: "flex-start" }}
                          >
                            → {next}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Shell>
  );
}
